import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import type { TicketRow, TicketViolationRow } from '../../types/safetySystem';
import { shortDate } from './SafetyUnits.columns';

/**
 * Tickets grid column sets — port of the V1 Ticket / Violations layouts
 * (defs_safety.pl 1556-1601 field lists, mirrored by tickets_query.py).
 */

const shortDateTime = (value: unknown): string => {
  const date = shortDate(value);
  if (!date) return '';
  const m = String(value).match(/[T ](\d{2}):(\d{2})/);
  return m ? `${date} ${m[1]}:${m[2]}` : date;
};

/** Y/N (or 0/1 EXISTS flags) as a small chip; blank renders empty. `yesTone` colours the Yes case. */
export function YnChip({ value, yesTone = 'neutral' }: { value: unknown; yesTone?: 'neutral' | 'danger' | 'accent' }) {
  const s = value === true || value === 1 || value === '1' ? 'Y' : value === false || value === 0 || value === '0' ? 'N' : String(value ?? '').toUpperCase();
  if (s !== 'Y' && s !== 'N') return null;
  const yes = s === 'Y';
  const cls = yes
    ? yesTone === 'danger' ? 'bg-danger text-danger-fg' : yesTone === 'accent' ? 'bg-accent text-accent-fg' : 'bg-gray-200 text-heading dark:bg-gray-700'
    : 'border border-rule text-muted';
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold leading-4 ${cls}`}>{yes ? 'Y' : 'N'}</span>;
}

type AnyRow = TicketRow | TicketViolationRow;

const dateCol = <T extends AnyRow>(accessorKey: string, header: string, size = 110): ColumnDef<T> => ({
  accessorKey, header, size, cell: ({ getValue }) => shortDate(getValue()),
});
const dateTimeCol = <T extends AnyRow>(accessorKey: string, header: string, size = 140): ColumnDef<T> => ({
  accessorKey, header, size, cell: ({ getValue }) => shortDateTime(getValue()),
});
const ynCol = <T extends AnyRow>(accessorKey: string, header: string, yesTone: 'neutral' | 'danger' | 'accent' = 'neutral', size = 70): ColumnDef<T> => ({
  accessorKey, header, size, cell: ({ getValue }) => <YnChip value={getValue()} yesTone={yesTone} />,
});
const numCol = <T extends AnyRow>(accessorKey: string, header: string, size = 90): ColumnDef<T> => ({
  accessorKey, header, size, meta: { align: 'right' },
});

export const TICKET_COLUMNS: ColumnDef<TicketRow>[] = [
  { accessorKey: 'ticketnum', header: 'Ticket #', size: 120 },
  { accessorKey: 'tdrivername', header: 'Driver', size: 200 },
  numCol('pronumber', 'Pro #', 100),
  dateCol('ticketstartdate', 'Ticket Date'),
  {
    accessorKey: 'closedatetime', header: 'Status', size: 90,
    cell: ({ getValue }) => {
      const v = String(getValue() ?? '');
      const closed = !!v && !v.startsWith('0000') && v !== '0';
      return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold leading-4 ${closed ? 'bg-gray-200 text-heading dark:bg-gray-700' : 'bg-accent text-accent-fg'}`}>{closed ? 'CLOSED' : 'OPEN'}</span>;
    },
  },
  ynCol('ticketoos', 'OOS', 'danger'),
  ynCol('ticketclean', 'Clean', 'accent'),
  { accessorKey: 'ticketlevel', header: 'Level', size: 70 },
  numCol('totticketweight', 'Weight'),
  { accessorKey: 'inspectedbyname', header: 'Inspected By', size: 160 },
  { accessorKey: 'ticketprecinct', header: 'Precinct', size: 100 },
  { accessorKey: 'ticketcounty', header: 'County', size: 120 },
  { accessorKey: 'tickethwy', header: 'Hwy', size: 90 },
  { accessorKey: 'milemarker', header: 'Mile Marker', size: 100 },
  { accessorKey: 'ticketstarttime', header: 'Start Time', size: 90 },
  { accessorKey: 'ticketendtime', header: 'End Time', size: 90 },
  dateCol('statecopyrcvddate', 'State Copy Rcvd'),
  dateCol('physcopyrcvddate', 'Phys Copy Rcvd'),
  dateCol('ticketsentdate', 'Sent Date'),
  { accessorKey: 'region', header: 'Region', size: 110 },
  { accessorKey: 'preference', header: 'Preference', size: 100 },
  { accessorKey: 'companyrep', header: 'Company Rep', size: 130 },
  { accessorKey: 'unitnumber', header: 'Unit', size: 80 },
  numCol('incidentrecno', 'Incident #', 100),
  { accessorKey: 'closedby', header: 'Closed By', size: 120 },
  { accessorKey: 'createdby', header: 'Created By', size: 120 },
  dateTimeCol('createdatetime', 'Created'),
  ynCol('ticketdeleted', 'Deleted'),
];

export const TICKET_DEFAULT_HIDDEN: VisibilityState = {
  ticketstarttime: false,
  ticketendtime: false,
  preference: false,
  companyrep: false,
  unitnumber: false,
  closedby: false,
  createdby: false,
  createdatetime: false,
  ticketdeleted: false,
};

export const VIOLATION_COLUMNS: ColumnDef<TicketViolationRow>[] = [
  { accessorKey: 'ticketnum', header: 'Ticket #', size: 120 },
  { accessorKey: 'tdrivername', header: 'Driver', size: 200 },
  numCol('pronumber', 'Pro #', 100),
  ynCol('wasticketed', 'Ticketed'),
  { accessorKey: 'violation', header: 'Violation', size: 110 },
  { accessorKey: 'violationdesc', header: 'Description', size: 260 },
  { accessorKey: 'violationunit', header: 'Unit', size: 70 },
  ynCol('violationoos', 'OOS', 'danger'),
  numCol('severityweight', 'Severity', 80),
  numCol('timeweight', 'Time', 70),
  numCol('totalweight', 'Total', 80),
  { accessorKey: 'ticketequipunit', header: 'Equip Unit', size: 90 },
  { accessorKey: 'ticketequiptype', header: 'Equip Type', size: 100 },
  { accessorKey: 'ticketequipmake', header: 'Make', size: 100 },
  { accessorKey: 'ticketequipco', header: 'Company', size: 110 },
  { accessorKey: 'ticketequipplate', header: 'Plate', size: 100 },
  { accessorKey: 'ticketequipstate', header: 'State', size: 70 },
  { accessorKey: 'ticketequipvin', header: 'VIN', size: 170 },
  { accessorKey: 'ticketequipowner', header: 'Owner', size: 100 },
];

export const VIOLATION_DEFAULT_HIDDEN: VisibilityState = {};

export const TICKETS_DATE_COLUMNS = ['ticketstartdate', 'statecopyrcvddate', 'physcopyrcvddate', 'ticketsentdate', 'createdatetime'];
